import { useMemo } from "react";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";

import { FormInput, FormSelect, FormToggle } from "components/BaseForm";
import { buildColumnFormSchema } from "routes/custom_tables/columnFormSchema";
import ChoiceListField from "routes/custom_tables/modals/ChoiceListField";
import { deriveFieldId, formatError } from "routes/custom_tables/helpers";
import { typeLabel } from "routes/custom_tables/typeLabels";
import { resolveRelationshipTarget } from "routes/custom_tables/relationshipRoutes";
import { useRelationshipTargets } from "hooks/useRelationshipTargets";
import {
  AlignLeft,
  Calendar,
  CircleDot,
  DollarSign,
  Hash,
  Link,
  Link2,
  List,
  Mail,
  Percent,
  Phone,
  Tags,
  ToggleRight,
  Type,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { SaveButton } from "@/components/ui/save-button";
import { cn } from "@/lib/utils";

const FIELD_TYPES = [
  {
    type: "short_text",
    description: "Single line, max 512 characters",
    icon: Type,
  },
  { type: "text", description: "Multi-line, up to 10 KB", icon: AlignLeft },
  { type: "number", description: "Integer or decimal value", icon: Hash },
  { type: "date", description: "Calendar date", icon: Calendar },
  { type: "boolean", description: "True / false toggle", icon: ToggleRight },
  {
    type: "single_select",
    description: "Pick one option from a list",
    icon: CircleDot,
  },
  { type: "multi_select", description: "Pick multiple options", icon: Tags },
  { type: "list", description: "Free-form list of strings", icon: List },
  {
    type: "relationship",
    description: "Link to another record",
    icon: Link2,
  },
  { type: "email", description: "Email address", icon: Mail },
  { type: "url", description: "Web link (http/https)", icon: Link },
  { type: "phone", description: "Phone number", icon: Phone },
  {
    type: "percentage",
    description: "Numeric percent (85 = 85%)",
    icon: Percent,
  },
  { type: "currency", description: "USD amount", icon: DollarSign },
];

const SELECT_TYPES = new Set(["single_select", "multi_select"]);
const isUniqueDisabled = type => type === "boolean" || type === "multi_select";

const EMPTY_DEFAULTS = {
  display_name: "",
  field_id: "",
  type: "short_text",
  is_required: false,
  is_unique: false,
  choices: [],
  target_type: null,
  label_field_id: null,
};

const FieldForm = ({
  mode,
  column,
  existingFieldIds,
  rowCount,
  onSubmit,
  onClose,
}) => {
  const isEditMode = mode === "edit";
  const schema = useMemo(
    () => buildColumnFormSchema({ existingFieldIds, mode }),
    [existingFieldIds, mode]
  );
  const defaultValues = useMemo(
    () =>
      column
        ? {
            display_name: column.display_name ?? "",
            field_id: column.field_id ?? "",
            type: column.type ?? "short_text",
            is_required: !!column.is_required,
            is_unique: !!column.is_unique,
            choices: column.options?.choices ?? [],
            target_type: column.options?.target_type ?? null,
            label_field_id: column.options?.label_field_id ?? null,
          }
        : EMPTY_DEFAULTS,
    [column]
  );

  const methods = useForm({
    resolver: zodResolver(schema),
    defaultValues,
    mode: "onChange",
  });
  const { control, handleSubmit, setValue, getValues, formState } = methods;
  const { isSubmitting, isValid, isDirty } = formState;

  const type = useWatch({ control, name: "type" });
  const isSelect = SELECT_TYPES.has(type);
  const isRelationship = type === "relationship";
  const uniqueDisabled = isUniqueDisabled(type);
  const isUnique = useWatch({ control, name: "is_unique" });

  const { targetOptions, labelFieldOptionsFor } = useRelationshipTargets(
    isRelationship && !isEditMode
  );
  const targetType = useWatch({ control, name: "target_type" });
  const labelFieldOptions = labelFieldOptionsFor(targetType);

  const showBackfillWarning = isUnique && !uniqueDisabled && rowCount > 0;

  const submit = async values => {
    const payload = {
      display_name: values.display_name.trim(),
      is_required: values.is_required,
      is_unique: uniqueDisabled ? false : values.is_unique,
    };
    if (!isEditMode) {
      payload.field_id = values.field_id;
      payload.type = values.type;
    }
    if (isSelect) payload.options = { choices: values.choices };
    if (isRelationship && !isEditMode) {
      payload.options = {
        target_type: values.target_type,
        ...(values.label_field_id
          ? { label_field_id: values.label_field_id }
          : {}),
      };
    }
    try {
      await onSubmit(payload);
      onClose();
    } catch (err) {
      // Server errors only surface here in edit mode (create appends to the
      // draft field array and never throws). Keep the dialog open for a retry.
      const affected =
        err?.response?.data?.affected_row_ids ?? err?.body?.affected_row_ids;
      const description =
        Array.isArray(affected) && affected.length
          ? `Used by row id(s): ${affected.slice(0, 10).join(", ")}${
              affected.length > 10 ? ` (+${affected.length - 10} more)` : ""
            }`
          : undefined;
      toast.error(
        formatError(
          err,
          isEditMode ? "Failed to update field" : "Failed to add field"
        ),
        description ? { description } : undefined
      );
    }
  };

  const submitDisabled = isSubmitting || !isValid || (isEditMode && !isDirty);

  return (
    <FormProvider {...methods}>
      <div className="tw:flex tw:flex-col tw:gap-4 tw:max-h-[70vh] tw:overflow-y-auto tw:pb-2">
        {isEditMode ? (
          <>
            <div className="tw:flex tw:items-center tw:gap-2">
              <span className="tw:min-w-20 tw:text-sm tw:font-semibold tw:text-muted-foreground">
                Type
              </span>
              <Badge variant="secondary">{typeLabel(column.type)}</Badge>
            </div>
            <div className="tw:flex tw:items-center tw:gap-2">
              <span className="tw:min-w-20 tw:text-sm tw:font-semibold tw:text-muted-foreground">
                Field ID
              </span>
              <Badge variant="secondary" code>
                {column.field_id}
              </Badge>
            </div>
            {column.type === "relationship" && (
              <div className="tw:flex tw:items-center tw:gap-2">
                <span className="tw:min-w-20 tw:text-sm tw:font-semibold tw:text-muted-foreground">
                  Links to
                </span>
                <Badge variant="secondary">
                  {resolveRelationshipTarget(
                    column.options?.target_type,
                    column.options
                  )?.label ?? column.options?.target_type}
                </Badge>
              </div>
            )}
          </>
        ) : (
          <Controller
            name="type"
            control={control}
            render={({ field }) => (
              <div
                role="radiogroup"
                aria-label="Field type"
                className="tw:grid tw:grid-cols-4 tw:gap-2"
              >
                {FIELD_TYPES.map(ft => {
                  const selected = field.value === ft.type;
                  return (
                    <button
                      key={ft.type}
                      type="button"
                      aria-pressed={selected}
                      aria-label={typeLabel(ft.type)}
                      className={cn(
                        "tw:flex tw:flex-col tw:items-start tw:gap-1 tw:p-3 tw:rounded-md tw:border tw:border-border tw:bg-card tw:text-foreground tw:cursor-pointer tw:text-left tw:transition-colors tw:duration-150 tw:hover:border-muted-foreground tw:hover:bg-[var(--color-surface-raised)] tw:focus-visible:outline-2 tw:focus-visible:outline-ring tw:focus-visible:outline-offset-1",
                        selected &&
                          "tw:border-primary tw:bg-[var(--color-surface-raised)]"
                      )}
                      onClick={() => {
                        field.onChange(ft.type);
                        if (isUniqueDisabled(ft.type))
                          setValue("is_unique", false);
                        if (ft.type !== "relationship") {
                          setValue("target_type", null);
                          setValue("label_field_id", null);
                        }
                      }}
                    >
                      <ft.icon className="tw:size-5" aria-hidden="true" />
                      <span
                        className="tw:mt-1 tw:font-semibold"
                        aria-hidden="true"
                      >
                        {typeLabel(ft.type)}
                      </span>
                      <span
                        className="tw:text-xs tw:leading-snug tw:text-muted-foreground"
                        aria-hidden="true"
                      >
                        {ft.description}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          />
        )}

        <Controller
          name="display_name"
          control={control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.error ? true : undefined}>
              <FieldLabel htmlFor="field-display-name">Display name</FieldLabel>
              <Input
                id="field-display-name"
                autoFocus
                value={field.value ?? ""}
                onBlur={field.onBlur}
                placeholder="e.g. Source"
                onChange={e => {
                  const next = e.target.value;
                  const prev = getValues("display_name");
                  const currentFieldId = getValues("field_id");
                  field.onChange(next);
                  // Keep auto-deriving field_id until the user overrides it —
                  // i.e. while it still matches the previous display name's
                  // derivation (or is empty). shouldValidate keeps isValid live.
                  if (
                    !isEditMode &&
                    (!currentFieldId || currentFieldId === deriveFieldId(prev))
                  ) {
                    setValue("field_id", deriveFieldId(next), {
                      shouldValidate: true,
                    });
                  }
                }}
              />
              <FieldError>{fieldState.error?.message}</FieldError>
            </Field>
          )}
        />

        {!isEditMode && (
          <FormInput
            name="field_id"
            label="Field ID"
            placeholder="auto-generated from display name"
            className="tw:font-mono"
          />
        )}

        <div className="tw:flex tw:flex-col tw:gap-3">
          <FormToggle name="is_required" label="Required" />
          <div className="tw:flex tw:flex-col tw:gap-1">
            <FormToggle
              name="is_unique"
              label="Unique"
              disabled={uniqueDisabled}
            />
            {uniqueDisabled && (
              <span className="tw:text-xs tw:text-muted-foreground">
                Unique constraints are not supported for{" "}
                {type === "boolean" ? "boolean" : "multi-select"} fields.
              </span>
            )}
          </div>
        </div>

        {showBackfillWarning && (
          <div className="tw:rounded-md tw:border-l-4 tw:border-l-primary tw:bg-accent tw:px-3 tw:py-2 tw:text-sm tw:text-foreground">
            This table has {rowCount} existing row
            {rowCount === 1 ? "" : "s"}. Adding a unique constraint will fail if
            any duplicate values already exist.
          </div>
        )}

        {isSelect && (
          <ChoiceListField
            name="choices"
            label="Choices"
            placeholder="Add a choice and press Enter"
          />
        )}

        {isRelationship && !isEditMode && (
          <>
            <Controller
              name="target_type"
              control={control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.error ? true : undefined}>
                  <FieldLabel htmlFor="field-target">Links to</FieldLabel>
                  <Select
                    value={field.value ?? null}
                    onValueChange={next => {
                      field.onChange(next);
                      setValue("label_field_id", null);
                    }}
                  >
                    <SelectTrigger
                      id="field-target"
                      aria-label="Links to"
                      className="tw:w-full"
                    >
                      <SelectValue placeholder="Pick a record type…">
                        {value =>
                          targetOptions.find(o => o.value === value)?.label ??
                          "Pick a record type…"
                        }
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {targetOptions.map(o => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FieldError>{fieldState.error?.message}</FieldError>
                </Field>
              )}
            />
            {labelFieldOptions.length > 0 && (
              <FormSelect
                name="label_field_id"
                label="Label column (optional)"
                clearable
                placeholder="First text column (default)"
                options={labelFieldOptions.map(o => ({
                  value: o.value,
                  text: o.label,
                }))}
              />
            )}
          </>
        )}
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <SaveButton
          onClick={handleSubmit(submit)}
          saving={isSubmitting}
          disabled={submitDisabled}
        >
          {isEditMode ? "Save" : "Add field"}
        </SaveButton>
      </DialogFooter>
    </FormProvider>
  );
};

const FieldDialog = ({
  open,
  onOpenChange,
  mode = "add",
  column = null,
  existingFieldIds = [],
  rowCount = 0,
  onSubmit,
}) => {
  const isEditMode = mode === "edit";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        aria-label={isEditMode ? "Edit field" : "Add field"}
        className={isEditMode ? undefined : "tw:sm:max-w-2xl"}
      >
        <DialogTitle>{isEditMode ? "Edit field" : "Add field"}</DialogTitle>
        {open && (
          <FieldForm
            key={`${mode}:${column?.field_id ?? "new"}`}
            mode={mode}
            column={column}
            existingFieldIds={existingFieldIds}
            rowCount={rowCount}
            onSubmit={onSubmit}
            onClose={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default FieldDialog;
